import { useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import type { UsageTrendData } from '@/lib/api/types'

interface UsageTrendChartProps {
  data: UsageTrendData[]
  loading?: boolean
}

type MetricKey = 'views' | 'downloads' | 'generations'

const METRICS: { key: MetricKey; label: string; color: string; bgColor: string }[] = [
  { key: 'views', label: 'Views', color: '#3B82F6', bgColor: 'bg-blue-500' },
  { key: 'downloads', label: 'Downloads', color: '#10B981', bgColor: 'bg-emerald-500' },
  { key: 'generations', label: 'Generations', color: '#8B5CF6', bgColor: 'bg-violet-500' },
]

export function UsageTrendChart({ data, loading }: UsageTrendChartProps) {
  const [activeMetrics, setActiveMetrics] = useState<Set<MetricKey>>(
    new Set(['views', 'downloads', 'generations'])
  )
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null)

  const toggleMetric = (key: MetricKey) => {
    const newSet = new Set(activeMetrics)
    if (newSet.has(key)) {
      if (newSet.size > 1) newSet.delete(key)
    } else {
      newSet.add(key)
    }
    setActiveMetrics(newSet)
  }

  // Calculate max value for scaling
  const maxValue = Math.max(
    ...data.flatMap((d) => [
      activeMetrics.has('views') ? d.views : 0,
      activeMetrics.has('downloads') ? d.downloads : 0,
      activeMetrics.has('generations') ? d.generations : 0,
    ]),
    1
  )

  // Generate chart points
  const chartHeight = 200
  const chartPadding = 40

  const getY = (value: number) => {
    return chartHeight - chartPadding - (value / maxValue) * (chartHeight - chartPadding * 2)
  }

  const getPath = (key: MetricKey) => {
    if (data.length === 0) return ''
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1 || 1)) * 100
      const y = getY(d[key])
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
    })
    return points.join(' ')
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Trends</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Template activity over time</p>
          </div>
        </div>
        <div className="h-[200px] bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
      </FluentCard>
    )
  }

  return (
    <FluentCard>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Trends</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Template activity over time</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4">
          {METRICS.map((metric) => (
            <button
              key={metric.key}
              onClick={() => toggleMetric(metric.key)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm font-medium',
                activeMetrics.has(metric.key)
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                  : 'text-gray-400 dark:text-gray-500'
              )}
            >
              <span className={cn('w-3 h-3 rounded-full', metric.bgColor)} />
              {metric.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="relative h-[200px]">
        {data.length > 0 ? (
          <svg
            viewBox={`0 0 100 ${chartHeight}`}
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
              <line
                key={ratio}
                x1="0"
                y1={getY(maxValue * ratio)}
                x2="100"
                y2={getY(maxValue * ratio)}
                stroke="currentColor"
                strokeOpacity={0.1}
                className="text-gray-400"
              />
            ))}

            {/* Area fills */}
            {METRICS.map(
              (metric) =>
                activeMetrics.has(metric.key) && (
                  <path
                    key={`area-${metric.key}`}
                    d={`${getPath(metric.key)} L 100 ${chartHeight - chartPadding} L 0 ${chartHeight - chartPadding} Z`}
                    fill={metric.color}
                    fillOpacity={0.1}
                  />
                )
            )}

            {/* Lines */}
            {METRICS.map(
              (metric) =>
                activeMetrics.has(metric.key) && (
                  <path
                    key={`line-${metric.key}`}
                    d={getPath(metric.key)}
                    fill="none"
                    stroke={metric.color}
                    strokeWidth="0.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )
            )}

            {/* Hover points */}
            {data.map((d, i) => {
              const x = (i / (data.length - 1 || 1)) * 100
              return (
                <g key={i}>
                  <rect
                    x={x - 3}
                    y={chartPadding}
                    width={6}
                    height={chartHeight - chartPadding * 2}
                    fill="transparent"
                    onMouseEnter={() => setHoveredPoint(i)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    style={{ cursor: 'pointer' }}
                  />
                  {hoveredPoint === i && (
                    <line
                      x1={x}
                      y1={chartPadding}
                      x2={x}
                      y2={chartHeight - chartPadding}
                      stroke="currentColor"
                      strokeOpacity={0.2}
                      strokeDasharray="2,2"
                      className="text-gray-400"
                    />
                  )}
                  {hoveredPoint === i &&
                    METRICS.map(
                      (metric) =>
                        activeMetrics.has(metric.key) && (
                          <circle
                            key={metric.key}
                            cx={x}
                            cy={getY(d[metric.key])}
                            r="1"
                            fill={metric.color}
                            stroke="white"
                            strokeWidth="0.5"
                          />
                        )
                    )}
                </g>
              )
            })}
          </svg>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
            <p>No data available for this period</p>
          </div>
        )}

        {/* Tooltip */}
        {hoveredPoint !== null && data[hoveredPoint] && (
          <div
            className="absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 px-3 py-2 z-10 pointer-events-none"
            style={{
              left: `${(hoveredPoint / (data.length - 1 || 1)) * 100}%`,
              top: '0',
              transform: 'translateX(-50%)',
            }}
          >
            <p className="text-xs font-medium text-gray-900 dark:text-white mb-1">
              {formatDate(data[hoveredPoint].date)}
            </p>
            {METRICS.map(
              (metric) =>
                activeMetrics.has(metric.key) && (
                  <div key={metric.key} className="flex items-center gap-2 text-xs">
                    <span className={cn('w-2 h-2 rounded-full', metric.bgColor)} />
                    <span className="text-gray-500 dark:text-gray-400">{metric.label}:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {hoveredPoint !== null && data[hoveredPoint] ? data[hoveredPoint][metric.key].toLocaleString() : ''}
                    </span>
                  </div>
                )
            )}
          </div>
        )}

        {/* X-axis labels */}
        {data.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-400">
            <span>{formatDate(data[0]?.date ?? '')}</span>
            {data.length > 2 && (
              <span>{formatDate(data[Math.floor(data.length / 2)]?.date ?? '')}</span>
            )}
            <span>{formatDate(data[data.length - 1]?.date ?? '')}</span>
          </div>
        )}
      </div>
    </FluentCard>
  )
}
